const RELOAD_STORAGE_KEY = "therecruit.chunk-recovery.last-reload";
const RELOAD_THROTTLE_MS = 60_000;

type ChunkRecoveryDependencies = {
  storage: Pick<Storage, "getItem" | "setItem">;
  reload: () => void;
  now?: () => number;
};

/**
 * Recovers an open tab when a deployment removes a lazy-loaded chunk referenced
 * by the older HTML document. Vite emits this event before surfacing the import
 * failure. The timestamp prevents a broken deployment from causing a reload loop.
 */
export function createChunkRecoveryHandler({
  storage,
  reload,
  now = Date.now,
}: ChunkRecoveryDependencies): (event: Event) => void {
  return (event: Event) => {
    const currentTime = now();
    let previousReload = 0;

    try {
      previousReload = Number(storage.getItem(RELOAD_STORAGE_KEY) ?? 0);
    } catch {
      // Browsers can deny storage access. Reloading still gives the tab one
      // opportunity to fetch the current HTML shell.
    }

    if (currentTime - previousReload < RELOAD_THROTTLE_MS) {
      return;
    }

    event.preventDefault();

    try {
      storage.setItem(RELOAD_STORAGE_KEY, String(currentTime));
    } catch {
      // Storage is an optimization for loop prevention, not a prerequisite.
    }

    reload();
  };
}

export function installChunkRecovery(): void {
  if (typeof window === "undefined") return;

  const handler = createChunkRecoveryHandler({
    storage: window.sessionStorage,
    reload: () => window.location.reload(),
  });

  window.addEventListener("vite:preloadError", handler);
}
